import { A, useNavigate } from '@solidjs/router';
import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { EmptyState, ErrorAlert, LoadingRows } from '../components/states';
import { ARCHITECTURES, INSTANCE_TYPES, OPERATING_SYSTEMS, OS_VERSIONS } from '../lib/catalog';
import { deployOrder } from '../lib/format';
import { flatten, useCreateBox, useSshKeys, useStacks } from '../lib/queries';
import { pushToast } from '../lib/toast';
import type { Ec2Arch, Ec2OS, StackWithManifest } from '../lib/types';
import { Button, buttonClass } from '../ui/Button';
import { Card } from '../ui/Card';
import { CheckboxCard, Input, Select } from '../ui/Field';
import { Icon } from '../ui/Icon';

type DomainType = 'subdomain' | 'b_y_o_domain';

const DOMAIN_TYPES = [
  { value: 'subdomain', label: 'Managed subdomain' },
  { value: 'b_y_o_domain', label: 'Bring your own domain' },
];

function subdomainFromDisplay(display: string): string {
  return display.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function BoxCreate(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateBox();
  const keys = useSshKeys();
  const stacks = useStacks();
  const keyItems = createMemo(() => flatten(keys.data?.pages));
  const stackItems = createMemo(() =>
    flatten(stacks.data?.pages).filter((stack): stack is StackWithManifest => stack.manifest === '1'),
  );

  const [display, setDisplay] = createSignal('');
  const [touched, setTouched] = createSignal(false);
  const [arch, setArch] = createSignal<Ec2Arch>('X86_64');
  const [instanceType, setInstanceType] = createSignal(INSTANCE_TYPES.X86_64[1]!.value);
  const [os, setOs] = createSignal<Ec2OS>('Ubuntu');
  const [osVersion, setOsVersion] = createSignal(OS_VERSIONS.Ubuntu[0]!.value);
  const [domainType, setDomainType] = createSignal<DomainType>('subdomain');
  const [subdomain, setSubdomain] = createSignal('');
  const [customDomain, setCustomDomain] = createSignal('');
  const [subdomainEdited, setSubdomainEdited] = createSignal(false);
  const [selectedKeys, setSelectedKeys] = createSignal<string[]>([]);
  const [selectedStacks, setSelectedStacks] = createSignal<string[]>([]);

  createEffect(() => {
    if (stacks.hasNextPage && !stacks.isFetchingNextPage) void stacks.fetchNextPage();
  });
  // Everything starts selected, once the whole catalog has loaded.
  let stacksPreselected = false;
  createEffect(() => {
    if (stacksPreselected || !stacks.isSuccess || stacks.hasNextPage) return;
    stacksPreselected = true;
    setSelectedStacks(deployOrder(stackItems().map((stack) => stack.slug)));
  });

  const instanceChoices = createMemo(() => INSTANCE_TYPES[arch()]);
  const domain = () => (domainType() === 'subdomain' ? subdomain() : customDomain());
  const allStacksSelected = () => stackItems().length > 0 && selectedStacks().length === stackItems().length;

  function changeDisplay(next: string): void {
    setDisplay(next);
    if (!subdomainEdited()) setSubdomain(subdomainFromDisplay(next));
  }

  function changeDomain(next: string): void {
    if (domainType() === 'subdomain') {
      setSubdomainEdited(true);
      setSubdomain(next);
    } else {
      setCustomDomain(next);
    }
  }

  function changeArch(next: Ec2Arch): void {
    setArch(next);
    setInstanceType(INSTANCE_TYPES[next][0]!.value);
  }

  function toggleKey(hash: string, checked: boolean): void {
    setSelectedKeys((current) => (checked ? [...current, hash] : current.filter((item) => item !== hash)));
  }

  function toggleStack(slug: string, checked: boolean): void {
    setSelectedStacks((current) => (checked ? deployOrder([...current, slug]) : current.filter((item) => item !== slug)));
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    setTouched(true);
    if (!display().trim() || !domain().trim()) return;
    try {
      const result = await create.mutateAsync({
        display: display().trim(),
        instance_type: instanceType(),
        os: os(),
        os_version: osVersion(),
        arch: arch(),
        ssh_keys: selectedKeys(),
        depl_stacks: deployOrder(selectedStacks()),
        domain: { type: domainType(), value: domain().trim() },
      });
      pushToast('info', 'Box requested. This usually takes a few minutes.');
      navigate(`/boxes/${result.box_id}`);
    } catch {
      // Shown by the error alert.
    }
  }

  return (
    <main class="page page--narrow">
      <A href="/boxes" class="backlink">
        <Icon name="arrowLeft" size={14} />
        Boxes
      </A>
      <div class="page__head">
        <div class="page__title">
          <h1>New box</h1>
          <span class="page__subtitle">
            An Ubuntu machine with Docker, a public address and your SSH keys installed.
          </span>
        </div>
      </div>

      <form class="form" onSubmit={submit}>
        <Card padding="lg">
          <div class="form">
            <Input
              label="Display name"
              required
              value={display()}
              onInput={(event) => changeDisplay(event.currentTarget.value)}
              error={touched() && !display().trim() ? 'Required.' : undefined}
            />
            <div class="form__row">
              <Select
                label="Hostname type"
                options={DOMAIN_TYPES}
                value={domainType()}
                onChange={(event) => setDomainType(event.currentTarget.value as DomainType)}
              />
              <Input
                label={domainType() === 'subdomain' ? 'Subdomain' : 'Domain'}
                required
                mono
                value={domain()}
                placeholder={domainType() === 'subdomain' ? 'my-box' : 'box.example.com'}
                onInput={(event) => changeDomain(event.currentTarget.value)}
                error={touched() && !domain().trim() ? 'Required.' : undefined}
                helperText={
                  domainType() === 'subdomain'
                    ? 'Creates DNS records under the managed platform domain.'
                    : 'Uses this hostname without creating DNS records.'
                }
              />
            </div>
            <div class="form__row">
              <Select
                label="Architecture"
                options={ARCHITECTURES}
                value={arch()}
                onChange={(event) => changeArch(event.currentTarget.value as Ec2Arch)}
              />
              <Select
                label="Instance type"
                options={instanceChoices()}
                value={instanceType()}
                onChange={(event) => setInstanceType(event.currentTarget.value)}
                helperText="Costs accrue until the box is destroyed."
              />
            </div>
            <div class="form__row">
              <Select
                label="Operating system"
                options={OPERATING_SYSTEMS}
                value={os()}
                onChange={(event) => setOs(event.currentTarget.value as Ec2OS)}
                disabled={OPERATING_SYSTEMS.length === 1}
              />
              <Select
                label="OS version"
                options={OS_VERSIONS[os()]}
                value={osVersion()}
                onChange={(event) => setOsVersion(event.currentTarget.value)}
              />
            </div>
          </div>
        </Card>

        <Card padding="lg">
          <div class="section__title">
            <div class="stack gap-1">
              <h3>SSH keys</h3>
              <span class="text-sm secondary">Selected keys are installed for the ubuntu user.</span>
            </div>
            <A href="/ssh-keys" class={buttonClass('ghost', 'sm')}>
              <Icon name="key" />
              Manage keys
            </A>
          </div>

          <Show when={!keys.isPending} fallback={<LoadingRows rows={2} height={64} />}>
            <Show
              when={keyItems().length > 0}
              fallback={
                <EmptyState
                  icon="key"
                  title="No SSH keys registered"
                  text="Without a key you will not be able to log in to the box."
                  action={
                    <A href="/ssh-keys" class={buttonClass('secondary', 'sm')}>
                      Add a key
                    </A>
                  }
                />
              }
            >
              <div class="key-picker">
                <For each={keyItems()}>
                  {(key) => (
                    <CheckboxCard
                      checked={selectedKeys().includes(key.hash)}
                      onChange={(checked) => toggleKey(key.hash, checked)}
                      title={key.display}
                      subtitle={key.hash}
                    />
                  )}
                </For>
              </div>
            </Show>
          </Show>

          <Show when={keyItems().length > 0 && selectedKeys().length === 0}>
            <div class="alert alert--warning" style={{ 'margin-top': 'var(--space-4)' }}>
              <Icon name="alert" size={18} />
              <div class="alert__body">
                <span>No key selected — you will not be able to SSH into this box.</span>
              </div>
            </div>
          </Show>
        </Card>

        <Card padding="lg">
          <div class="section__title">
            <div class="stack gap-1">
              <h3>Stacks</h3>
              <span class="text-sm secondary">Selected stacks are deployed after the box is provisioned.</span>
            </div>
            <Show when={stackItems().length > 0}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedStacks(allStacksSelected() ? [] : deployOrder(stackItems().map((stack) => stack.slug)))}
              >
                {allStacksSelected() ? 'Deselect all' : 'Select all'}
              </Button>
            </Show>
          </div>

          <Show when={!stacks.isPending} fallback={<LoadingRows rows={2} height={64} />}>
            <Show
              when={stackItems().length > 0}
              fallback={<EmptyState icon="layers" title="No stacks registered" text="Stacks can also be deployed after the box is ready." />}
            >
              <div class="key-picker" style={{ 'margin-top': 'var(--space-4)' }}>
                <For each={stackItems()}>
                  {(stack) => (
                    <CheckboxCard
                      checked={selectedStacks().includes(stack.slug)}
                      onChange={(checked) => toggleStack(stack.slug, checked)}
                      title={stack.display}
                      subtitle={`${stack.slug} · rev ${stack.revision}`}
                    />
                  )}
                </For>
              </div>
            </Show>
          </Show>

          <Show when={stacks.isError}>
            <ErrorAlert title="Could not load stacks" error={stacks.error} />
          </Show>
        </Card>

        <Show when={create.isError}>
          <ErrorAlert title="Could not create the box" error={create.error} />
        </Show>

        <div class="form__foot">
          <A href="/boxes" class={buttonClass('secondary', 'md')}>
            Cancel
          </A>
          <Button type="submit" loading={create.isPending}>
            <Icon name="plus" />
            Create box
          </Button>
        </div>
      </form>
    </main>
  );
}
